import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';

import { RdsChip } from '../chips/index';
import { RDS_TEXT_PARAMETER_OPTIONS, RdsTextFilter, RdsTextParameterOption } from './text-filter.model';

@Component({
  selector: 'rds-text-filter',
  templateUrl: './text-filter.component.html',
  styleUrls: ['./text-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RdsTextFilterComponent implements OnChanges {
  /** Text Filters */
  @Input() filters: Array<RdsTextFilter>;

  /** Whether to use the large chip style variant */
  @HostBinding('class.large-chips')
  @Input()
  largeChips = false;

  /** Event emitted when the user adds new filter */
  @Output() add = new EventEmitter<RdsTextFilter>();
  /** Event emitted when the user removes a filter */
  @Output() remove = new EventEmitter<RdsTextFilter>();

  inputControl = new FormControl('', Validators.minLength(1));

  /* Operators */
  enabledOptions: { [parameter: string]: boolean } = {
    matches: false,
    like: false,
    '=': true,
    '!=': true,
    '=NULL': true,
    '!=NULL': true,
  };

  /* Map of which parameters require text value */
  paramHasValue: { [operation: string]: boolean } = {
    '=': true,
    '!=': true,
    '!=NULL': false,
    '=NULL': false,
  };

  /* Parameter label and type */
  allOptions: ReadonlyArray<RdsTextParameterOption> = RDS_TEXT_PARAMETER_OPTIONS;

  /* Added filter operation and value from inputs */
  newValue = new RdsTextFilter(null, null);

  constructor(private cdr: ChangeDetectorRef) {}

  checkParameterAndText() {
    if (!!this.newValue.parameter && !this.paramHasValue[this.newValue.parameter.type]) {
      this.inputControl.reset('');
      this.inputControl.disable();
    }
    if (!!this.newValue.parameter && this.paramHasValue[this.newValue.parameter.type] && this.inputControl.disabled) {
      this.inputControl.enable();
    }
  }

  /**
   * Emit added values and clear the inputs
   */
  handleAdd() {
    const text = (this.inputControl.value as string).trim();
    if (!this.paramHasValue[this.newValue.parameter.type] || text.length) {
      this.newValue.text = text;
      this.add.emit(this.newValue);
      this.newValue = new RdsTextFilter(null, null);
      this.inputControl.reset(null);
    } else {
      this.inputControl.setValue(null);
      this.inputControl.setErrors({ minlength: true });
    }
  }

  handleRemove(chip: RdsChip) {
    const found = this.filters.find((f) => f.parameter.type + f.text === chip.id);
    this.remove.emit(found);
  }

  /**
   * Any time the filters change, determine which operations are still applicable, if any
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.filters) {
      this.enabledOptions = { matches: false, like: false, '=': true, '!=': true, '=NULL': true, '!=NULL': true };
      for (const f of changes.filters.currentValue) {
        switch (f.parameter.type) {
          case 'matches':
            this.enabledOptions['='] = false;
            this.enabledOptions['!='] = false;
            this.enabledOptions['=NULL'] = false;
            this.enabledOptions['!=NULL'] = false;
            this.enabledOptions['like'] = false;
            break;
          case 'like':
            this.enabledOptions['='] = false;
            this.enabledOptions['!='] = false;
            this.enabledOptions['=NULL'] = false;
            this.enabledOptions['!=NULL'] = false;
            this.enabledOptions['matches'] = false;
            break;
          case '=':
            this.enabledOptions['!='] = false;
            this.enabledOptions['=NULL'] = false;
            this.enabledOptions['!=NULL'] = false;
            this.enabledOptions['like'] = false;
            this.enabledOptions['matches'] = false;
            break;
          case '!=':
            this.enabledOptions['='] = false;
            this.enabledOptions['=NULL'] = false;
            this.enabledOptions['!=NULL'] = false;
            this.enabledOptions['like'] = false;
            this.enabledOptions['matches'] = false;
            break;
          case '=NULL':
            this.enabledOptions['='] = false;
            this.enabledOptions['!='] = false;
            this.enabledOptions['!=NULL'] = false;
            this.enabledOptions['=NULL'] = false;
            this.enabledOptions['like'] = false;
            this.enabledOptions['matches'] = false;
            break;
          case '!=NULL':
            this.enabledOptions['='] = false;
            this.enabledOptions['!='] = false;
            this.enabledOptions['=NULL'] = false;
            this.enabledOptions['!=NULL'] = false;
            this.enabledOptions['like'] = false;
            this.enabledOptions['matches'] = false;
            break;
        }
      }
      this.cdr.markForCheck();
    }
  }
}
